<template>
    <framework>
        <template #side>
            <side-panel-nav />
        </template>
        <template #main>
            <main-header title="Reactivate Courses" icon="fa-redo" />

            <div v-if="loading" class="has-text-white mt-6">
                <i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Loading your expired courses...
            </div>

            <div v-else-if="!courses.length" class="has-text-white mt-6">
                <p><i class="fas fa-info-circle mr-2"></i>No expired courses found. All your courses are up to date!</p>
                <router-link to="/enrollments" class="button is-primary mt-4">View Your Enrollments</router-link>
            </div>

            <div v-else>
                <!-- Info Banner -->
                <div class="info-banner mt-6">
                    <div class="columns is-vcentered">
                        <div class="column is-narrow is-hidden-mobile">
                            <i class="fas fa-info-circle fa-2x"></i>
                        </div>
                        <div class="column">
                            <p><strong>Course Reactivation</strong></p>
                            <p class="mt-2">Select courses by checking the box and choose how many hours you want. Hours you've already completed are <strong>free</strong> to reactivate. Additional hours are <strong>${{ pricePerHour }}/hour</strong>. Depending on the course, or how long it has been expired, reactivation may result in a new enrollment of the same course which will be noted below.</p>
                            <p class="mt-2"><strong>Note:</strong> Each course can only be reactivated once. However, you do not have to reactivate all of your courses at this time — you can return later to reactivate others.</p>
                        </div>
                    </div>
                </div>

                <!-- Expired Courses Header -->
                <div class="content mt-6">
                    <h4 class="has-text-white mb-4">Expired Courses ({{ courses.length }})</h4>
                </div>

                <!-- Course Cards -->
                <reactivation-course
                    v-for="course in courses"
                    :key="course.id"
                    :course="course"
                    :price-per-hour="pricePerHour"
                    v-model="selections[course.id]"
                    @input="updateSelection"
                    class="mb-4"
                />

                <!-- Summary Section (shown when courses selected) -->
                <div v-if="hasSelectedCourses" class="summary-section mt-6 mb-6 pb-6">
                    <div class="summary-card">
                        <h4 class="mb-4">Order Summary</h4>

                        <div class="summary-items">
                            <div v-for="item in selectedSummary" :key="item.id" class="summary-item">
                                <div class="item-name">{{ item.name }}</div>
                                <div class="item-details">
                                    {{ item.hours }} hours ({{ item.paidHours }} paid)
                                </div>
                                <div class="item-price" :class="{ 'is-free': item.price === 0 }">
                                    <template v-if="item.price > 0">{{ currency(item.price) }}</template>
                                    <template v-else-if="item.isGracePeriod">FREE (Grace Period)</template>
                                    <template v-else>FREE</template>
                                </div>
                            </div>
                        </div>

                        <hr />

                        <div class="summary-total">
                            <span>Total</span>
                            <span class="total-amount">{{ currency(totalAmount) }}</span>
                        </div>

                        <!-- Proceed to Checkout Button -->
                        <div v-if="!readyForCheckout" class="mt-5">
                            <b-button type="is-primary" expanded size="is-medium" @click="proceedToCheckout" :loading="preparingCheckout">
                                <i class="fas fa-arrow-right mr-2"></i>
                                Proceed to Checkout
                            </b-button>
                        </div>

                        <div v-if="readyForCheckout && taxAmount > 0" class="tax-note mt-4">
                            <i class="fas fa-info-circle mr-1"></i>
                            Includes {{ currency(taxAmount) }} sales tax
                            <span v-if="taxDetails">({{ (taxDetails.taxRate * 100).toFixed(2) }}%, {{ taxDetails.jurisdiction }})</span>
                        </div>
                    </div>
                </div>

                <!-- Checkout Section (shown after proceeding to checkout) -->
                <div v-if="readyForCheckout" class="content mt-6 pt-6">
                    <div class="columns is-gapless">
                        <div class="column is-narrow has-text-white is-hidden-mobile">
                            <i class="far fa-arrow-right-long fa-2x mr-5"></i>
                        </div>
                        <div class="column">
                            <h1 class="has-text-white mb-2">Checkout</h1>
                        </div>
                    </div>
                </div>

                <div v-if="readyForCheckout" class="cart-card">
                    <section v-if="!completed">
                        <div class="content">
                            <!-- Terms and Confirmations -->
                            <h4 class="mb-4">Terms and Acknowledgments</h4>

                            <b-message type="is-info" class="mt-5">
                                <div class="columns is-gapless mb-4">
                                    <div class="column is-narrow">
                                        <b-switch type="is-success" class="mr-4" v-model="ready.courseSelection"></b-switch>
                                    </div>
                                    <div class="column">
                                        I have reviewed all course and hour selections above and confirm they are accurate.
                                    </div>
                                </div>

                                <div class="columns is-gapless mb-4">
                                    <div class="column is-narrow">
                                        <b-switch type="is-success" class="mr-4" v-model="ready.pricing"></b-switch>
                                    </div>
                                    <div class="column">
                                        I understand the reactivation fee is ${{ pricePerHour }} per clock hour for hours beyond what I have already completed.
                                    </div>
                                </div>

                                <div class="columns is-gapless mb-4">
                                    <div class="column is-narrow">
                                        <b-switch type="is-success" class="mr-4" v-model="ready.refundPolicy"></b-switch>
                                    </div>
                                    <div class="column">
                                        I understand that reactivation fees are <u>non-refundable</u>.
                                    </div>
                                </div>

                                <div class="columns is-gapless mb-4">
                                    <div class="column is-narrow">
                                        <b-switch type="is-success" class="mr-4" v-model="ready.expiration"></b-switch>
                                    </div>
                                    <div class="column">
                                        I understand that reactivated courses will expire {{ expirationDate }} and I understand the clock hour offerings expire on September 30th of each year and will automatically renew until {{ expirationDate }} or clock hour approval is no longer available, whichever comes first.
                                    </div>
                                </div>
                            </b-message>

                            <!-- Signature -->
                            <h4 class="mt-6 mb-4">Signature</h4>
                            <div class="mb-4">
                                <p><i class="far fa-file-signature mr-3"></i>By signing below, I confirm that I have read and agree to all terms and conditions stated above. I authorize the charge to my payment method for the total amount shown.</p>
                            </div>

                            <div class="signature-container">
                                <div class="signature-pad">
                                    <div class="sig-line"></div>
                                    <div class="sig-x">
                                        <b-icon icon="times" />
                                    </div>
                                    <div class="sig-name" v-if="currentUser">{{ currentUser.first_name }} {{ currentUser.last_name }}</div>
                                    <div class="sig-buttons">
                                        <b-button size="is-small" @click="undoSignature" class="mr-2">
                                            <b-icon icon="undo" class="mr-1" />
                                            Undo
                                        </b-button>
                                        <b-button size="is-small" @click="clearSignature">
                                            <b-icon icon="eraser" class="mr-1" />
                                            Clear
                                        </b-button>
                                    </div>
                                    <vue-signature class="sig" ref="signature" :sigOption="signatureOption" :w="'1000px'" :h="'180px'" />
                                </div>
                            </div>

                            <hr />

                            <!-- Payment (only if not free) -->
                            <div v-if="totalAmount > 0">
                                <h4 class="mt-6 mb-4">Payment</h4>

                                <div v-if="!clientSecret && !isFree" class="pb-3">
                                    <i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Establishing secure connection for Credit Card...
                                </div>

                                <div v-show="clientSecret" class="pb-3">
                                    <div class="columns is-gapless">
                                        <div class="column is-narrow"><i class="far fa-info-circle mr-4"></i></div>
                                        <div class="column">
                                            <p>Your card will be charged <strong>{{ currency(totalAmount) }}</strong> for course reactivation.</p>

                                            <!-- Tax breakdown -->
                                            <div v-if="taxAmount > 0" class="mt-5 mb-4" style="background-color: #f8f9fa; padding: 1rem; border-radius: 4px; border: 1px solid #e0e2e8;">
                                                <table style="width: 100%; font-size: 0.95rem; border-collapse: collapse;">
                                                    <tr style="font-weight: 600;">
                                                        <td style="padding-bottom: 0.5rem; border: none;">Total:</td>
                                                        <td style="text-align: right; padding-bottom: 0.5rem; border: none;">{{ currency(totalAmount) }}</td>
                                                    </tr>
                                                    <tr v-if="taxDetails" style="font-size: 0.85rem; color: #6c757d;">
                                                        <td colspan="2" style="padding-top: 0.5rem; border: none; border-top: 1px solid #dee2e6;">
                                                            <i class="fas fa-info-circle mr-2"></i>Includes {{ currency(taxAmount) }} sales tax ({{ (taxDetails.taxRate * 100).toFixed(2) }}%, {{ taxDetails.jurisdiction }})
                                                        </td>
                                                    </tr>
                                                </table>
                                            </div>

                                            <div class="mt-3">
                                                <div class="card-container">
                                                    <div ref="card"></div>
                                                    <b-message type="is-danger" v-show="stripeErrors" class="mt-6">
                                                        <div id="card-errors"><i class="far fa-exclamation-triangle mr-3"></i>{{ stripeErrors }}</div>
                                                    </b-message>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Free Reactivation Message -->
                            <div v-else class="free-message mt-6">
                                <b-message type="is-success">
                                    <i class="fas fa-check-circle mr-2"></i>
                                    <strong>No payment required!</strong> You're only reactivating hours you've already completed.
                                </b-message>
                            </div>

                            <hr class="mt-6" />

                            <b-button size="is-large" type="is-primary" expanded class="mt-6" @click="checkout" :disabled="!isReadyForCheckout || processing">
                                <i v-if="!processing" class="fas fa-check mr-4"></i>
                                <i v-else class="fas fa-spin fa-spinner mr-4"></i>
                                {{ processing ? 'Processing...' : (isFree ? 'Complete Reactivation' : 'Submit Payment') }}
                            </b-button>

                            <div v-if="!isReadyForCheckout" class="mt-4" style="background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; padding: 1rem;">
                                <div style="font-weight: 600; margin-bottom: 0.5rem; color: #856404;">
                                    <i class="fas fa-exclamation-triangle mr-2"></i>Please complete the following:
                                </div>
                                <ul style="margin: 0; padding-left: 1.5rem; color: #856404;">
                                    <li v-if="!allConfirmationsReady" style="margin-bottom: 0.25rem;">Confirm all terms above</li>
                                    <li v-if="!signature" style="margin-bottom: 0.25rem;">Sign the agreement above</li>
                                    <li v-if="!isFree && !ccReady" style="margin-bottom: 0.25rem;">Complete your credit card information</li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    <section v-else>
                        <div class="pt-4">
                            <div class="columns">
                                <div class="column is-narrow">
                                    <i class="far fa-check-circle fa-3x has-text-primary"></i>
                                </div>
                                <div class="column is-9 content">
                                    <h3 class="mt-3">Your courses have been reactivated!</h3>

                                    <p class="mt-6">You can access your courses from your <strong><i class="far fa-book-reader mr-2 ml-1"></i>Course Enrollments</strong> at any time.</p>

                                    <div class="mt-6">
                                        <router-link to="/enrollments">
                                            <b-button type="is-primary">
                                                <span class="is-hidden-mobile">Continue to your <i class="far fa-book-reader mr-2 ml-1"></i>Course Enrollments<i class="far fa-long-arrow-right ml-3"></i></span>
                                                <span class="is-hidden-desktop"><i class="far fa-book-reader mr-2 ml-1"></i>Course Enrollments<i class="far fa-long-arrow-right ml-3"></i></span>
                                            </b-button>
                                        </router-link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </template>
    </framework>
</template>

<script>
import { client as http, stripePK } from "../../http_client";
import { mapGetters } from "vuex";
import async from "async";
import moment from "moment";
import Framework from "../../components/framework/Framework";
import SidePanelNav from "../../components/framework/SidePanelNav";
import MainHeader from "../../components/framework/MainHeader";
import VueSignature from 'vue-signature';
import ReactivationCourse from "../../components/ReactivationCourse";

let stripe = Stripe(stripePK),
    elements = null,
    card = null;

export default {
    components: { Framework, SidePanelNav, MainHeader, VueSignature, ReactivationCourse },
    data() {
        let _this = this;
        return {
            loading: true,
            processing: false,
            completed: false,
            courses: [],
            selections: {},
            pricePerHour: 5,
            gracePeriodDays: 5,
            gracePeriodExtensionDays: 5,
            readyForCheckout: false,
            preparingCheckout: false,
            clientSecret: null,
            intentId: null,
            taxAmount: 0,
            taxDetails: null,
            isFree: false,
            ccReady: false,
            stripeErrors: '',
            signature: null,
            ready: {
                courseSelection: false,
                pricing: false,
                refundPolicy: false,
                expiration: false
            },
            signatureOption: {
                penColor: 'rgb(0, 0, 0)',
                backgroundColor: 'rgb(255, 255, 255)',
                onEnd: function () {
                    _this.saveSignature();
                }
            }
        };
    },
    computed: {
        ...mapGetters(['currentUser']),
        hasSelectedCourses() {
            return Object.values(this.selections).some(s => s.selected);
        },
        selectedCourses() {
            return Object.values(this.selections).filter(s => s.selected);
        },
        selectedSummary() {
            return this.selectedCourses.map(sel => {
                const course = this.courses.find(c => c.id === sel.enrollmentId);
                const completedHours = course?.completedHours || 0;
                let paidHours = 0;
                let isGracePeriod = false;

                if (sel.hoursType === 'completed') {
                    paidHours = 0;
                } else if (course?.inGracePeriod) {
                    paidHours = 0;
                    isGracePeriod = true;
                } else {
                    paidHours = Math.max(0, sel.hours - completedHours);
                }

                return {
                    id: sel.enrollmentId,
                    name: course?.name || 'Course',
                    hours: sel.hours,
                    paidHours: paidHours,
                    price: paidHours * this.pricePerHour,
                    isGracePeriod: isGracePeriod
                };
            });
        },
        totalAmount() {
            return this.selectedSummary.reduce((sum, item) => sum + item.price, 0);
        },
        expirationDate() {
            // If all selected courses are completed hours only within grace period, use short extension
            const allGracePeriodCompleted = this.selectedCourses.every(s => {
                const course = this.courses.find(c => c.id === s.enrollmentId);
                return s.hoursType === 'completed' && course?.inGracePeriod;
            });

            if (allGracePeriodCompleted && this.selectedCourses.length > 0) {
                return moment().add(this.gracePeriodExtensionDays, 'days').format('MMMM D, YYYY');
            }
            return moment().add(1, 'year').format('MMMM D, YYYY');
        },
        allConfirmationsReady() {
            return this.ready.courseSelection && this.ready.pricing && this.ready.refundPolicy && this.ready.expiration;
        },
        isReadyForCheckout() {
            if (!this.allConfirmationsReady) return false;
            if (!this.signature) return false;
            if (!this.isFree && !this.ccReady) return false;
            return true;
        }
    },
    methods: {
        currency(value) {
            if (typeof value !== 'number') value = parseFloat(value) || 0;
            return '$' + value.toFixed(2);
        },
        loadCourses() {
            this.loading = true;
            http.get('app/courses/reactivation').then(response => {
                this.courses = response.data.courses || [];
                this.pricePerHour = response.data.pricePerHour || 5;
                this.gracePeriodDays = response.data.gracePeriodDays || 5;
                this.gracePeriodExtensionDays = response.data.gracePeriodExtensionDays || 5;

                // Initialize selections for each course
                this.courses.forEach(course => {
                    this.$set(this.selections, course.id, {
                        enrollmentId: course.id,
                        selected: false,
                        hours: course.originalUnits,
                        hoursType: 'full',
                        daysExpired: course.daysExpired,
                        inGracePeriod: course.inGracePeriod
                    });
                });

                this.loading = false;
            }).catch(err => {
                console.error('Error loading reactivation courses:', err);
                this.loading = false;
            });
        },
        updateSelection(data) {
            this.$set(this.selections, data.enrollmentId, data);

            // Reset intent when selections change - user will need to proceed to checkout again
            this.readyForCheckout = false;
            this.clientSecret = null;
            this.intentId = null;
        },
        proceedToCheckout() {
            if (!this.hasSelectedCourses || this.preparingCheckout) return;
            this.preparingCheckout = true;
            this.createIntent();
        },
        createIntent() {
            const selections = this.selectedCourses;
            if (!selections.length) return;

            http.post('app/courses/reactivation-intent', { selections }).then(response => {
                this.clientSecret = response.data.clientSecret;
                this.intentId = response.data.id;
                this.taxAmount = response.data.taxAmount || 0;
                this.taxDetails = response.data.taxDetails || null;
                this.isFree = response.data.isFree || false;
                this.readyForCheckout = true;
                this.preparingCheckout = false;

                // Only initialize Stripe if payment is required
                if (!this.isFree && this.clientSecret) {
                    this.$nextTick(() => {
                        this.stripeInit();
                    });
                }
            }).catch(err => {
                console.error('Error creating reactivation intent:', err);
                this.preparingCheckout = false;
            });
        },
        stripeInit() {
            if (!this.$refs.card) {
                console.error('Card ref not found');
                return;
            }

            // Clean up existing card element if any
            if (card) {
                card.unmount();
                card.destroy();
                card = null;
            }

            // Create fresh elements instance
            elements = stripe.elements();

            card = elements.create('card', {
                style: {
                    base: {
                        color: '#32325d',
                        fontSize: '16px',
                        '::placeholder': {
                            color: '#aab7c4',
                        }
                    },
                    invalid: {
                        color: '#ff3860',
                    }
                }
            });
            card.mount(this.$refs.card);

            card.on('change', event => {
                if (event.error) {
                    this.stripeErrors = event.error.message;
                } else {
                    this.stripeErrors = '';
                }
                this.ccReady = !!(event.complete);
            });
        },
        checkout() {
            if (this.processing) return;

            this.processing = true;
            this.stripeErrors = '';

            const selections = this.selectedCourses;

            async.series([
                // Confirm card payment (if not free)
                (cb) => {
                    if (this.isFree) return cb();

                    stripe.confirmCardPayment(this.clientSecret, {
                        payment_method: {
                            card: card
                        }
                    }).then(result => {
                        if (result.error) {
                            this.stripeErrors = result.error.message;
                            cb(result.error);
                        } else {
                            cb();
                        }
                    }).catch(cb);
                },
                // Complete checkout on backend
                (cb) => {
                    http.post('app/courses/reactivation-checkout', {
                        intent_id: this.intentId,
                        signature: this.signature,
                        selections: selections
                    }).then(response => {
                        this.completed = true;
                        cb();
                    }).catch(cb);
                }
            ], err => {
                if (err) {
                    console.error('Checkout error:', err);
                }
                this.processing = false;
            });
        },
        clearSignature() {
            this.$refs.signature.clear();
            this.signature = null;
        },
        undoSignature() {
            this.$refs.signature.undo();
            this.saveSignature();
        },
        saveSignature() {
            this.signature = this.$refs.signature.save();
        }
    },
    mounted() {
        if (!localStorage.getItem('token')) {
            this.$router.push('/');
            return;
        }
        this.loadCourses();
    },
    beforeDestroy() {
        // Clean up Stripe card element to prevent issues on remount
        if (card) {
            card.unmount();
            card.destroy();
            card = null;
        }
        elements = null;
    }
};
</script>

<style lang="scss" scoped>
.info-banner {
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 4px;
    padding: 1.5rem;
    box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);

    i {
        color: #336699;
    }
}

.summary-section {
    .summary-card {
        background-color: white;
        border-radius: 4px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
        padding: 1.5rem;

        h4 {
            color: #1e3a5f;
        }

        .summary-items {
            .summary-item {
                display: grid;
                grid-template-columns: 1fr auto auto;
                gap: 1rem;
                padding: 0.5rem 0;
                border-bottom: 1px solid #eee;

                &:last-child {
                    border-bottom: none;
                }

                .item-name {
                    font-weight: 500;
                }

                .item-details {
                    color: #666;
                    font-size: 0.9rem;
                }

                .item-price {
                    font-weight: 600;
                    text-align: right;

                    &.is-free {
                        color: #00d1b2;
                    }
                }
            }
        }

        .summary-total {
            display: flex;
            justify-content: space-between;
            font-size: 1.2rem;
            font-weight: 600;

            .total-amount {
                color: #1e3a5f;
            }
        }

        .tax-note {
            font-size: 0.85rem;
            color: #666;
        }
    }
}

// White card container for checkout section
.cart-card {
    background-color: white;
    border-radius: 4px;
    box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
    padding: 3.7rem 3.7rem 6rem;
    margin-bottom: 6rem;

    @media (max-width: 1180px) {
        padding: 2rem;
        margin-top: 0;
        margin-bottom: 4rem;
    }

    @media (max-width: 768px) {
        padding: 1rem;
    }
}

// Signature Container
.signature-container {
    overflow-x: auto;
    overflow-y: hidden;
    max-width: 100%;
}

// Signature Pad
.signature-pad {
    position: relative;
    background: white;
    max-width: 100%;
    margin-top: 2rem;

    .sig {
        border: none;
    }

    .sig-x {
        position: absolute;
        bottom: 45px;
        left: 20px;
        font-size: 1.5rem;
    }

    .sig-name {
        position: absolute;
        bottom: 20px;
        left: 50px;
        font-size: 1rem;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }

    .sig-line {
        position: absolute;
        bottom: 45px;
        left: 20px;
        right: 20px;
        border-bottom: 2px solid red;
    }

    .sig-buttons {
        position: absolute;
        top: 10px;
        right: 20px;
    }
}

// Card container for stripe element
.card-container {
    border: 1px solid #e7e7e7;
    border-radius: 8px;
    padding: 2rem;
    margin-bottom: 2rem;
}

.free-message {
    margin-bottom: 2rem;
}
</style>